/**
 * @file assign-product-to-vendors.tsx
 * @deprecated Copied to app/modules/procurement/pages/assign-product-to-vendors/assign-product-to-vendors.tsx. This file still serves the existing /UProcure routes.
 * @module app/modules/uProcure/assign-product-to-vendors/assign-product-to-vendors
 * @description Page component for assigning products to vendors in the uProcure section.
 *   Displays products with vendor selection, category filters, search, and pagination.
 *   Allows admins to assign or unassign products and variants to accepted vendors.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 *
 * @todo Sub/group/brand filters: frontend sends subCategories, groupCategories, and brands
 *   query params but utrade-backend listAllBusinessProducts only filters on categories.
 *   Add backend filter support or disable/remove unused params from fetchProducts.
 * @todo Brand filter: use selectedBrand?.value (not ._id) in fetchProducts callers; add
 *   handleBrandChange and uncomment brand CustomSelect when enabling the filter.
 * @todo Remove duplicate page title — index.tsx already renders PageTitle; drop the <h1>
 *   in this component.
 * @todo Cleanup dead code: unused Vendor/Product types, commented fetchProducts/vendor blocks,
 *   fetchBrands if brand UI stays disabled; remove or document stale manageProduct.assign*
 *   endpoints in apiEndPoints.ts (page correctly uses vendor.*).
 * @todo Add route-level permission guard for AssignProductToVendors in PrivateRoutes
 *   (sidebar already checks permission; direct URL may bypass it).
 * @todo Backend parity: utrade assignProductToVendor does not update vendor assigned-product
 *   counter (uprocure-backend does via updateAssignedProductsCounter).
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from "react";
import { Card, Row, Col, Button } from "react-bootstrap";
import { CustomSelect } from "../../../custom/Select/CustomSelect";
import {
  Brand,
  Categories,
  AllProduct,
  ApprovedProduct,
  PAGE_LIMIT,
} from "../../../../utils/constants";
import { manageProduct, master, vendor } from "../../../../api/apiEndPoints";
import APICallService from "../../../../api/apiCallService";
import Loader from "../../../../Global/loader";
import Pagination from "../../../../Global/pagination";
import { useLocation } from "react-router-dom";
import { getKey, setKey } from "../../../../Global/history";
import { error, success } from "../../../../Global/toast";
import { listAssignProductsToVendors } from "../../../../utils/storeString";
import { String } from "../../../../utils/string";
import { assignToVendorToast } from "../../../../utils/toast";
import { useDebounce } from "../../../../utils/useDebounce";
import { CustomSearchInput } from "../../../custom/search";

type Vendor = {
  id: string;
  name: string;
};

type Product = {
  id: string;
  name: string;
  sku?: string;
  price?: number;
};

type CategoryType = {
  _id: string;
  title: string;
  image: string;
  categories?: CategoryType[];
};

const AssignProductsToVendors: React.FC = () => {
  const depth = 3;
  const { state }: any = useLocation();
  const [vendorsList, setVendorList] = useState<any>([]);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  // const [page, setPage] = useState(1);
  // const [pageLimit, setPageLimit] = useState(PAGE_LIMIT);
  const [totalRecords, setTotalRecords] = useState(-1);
  const [products, setProducts] = useState<any>([]);
  // const [selectedVendorId, setSelectedVendorId] = useState("");

  const [loaderId, setLoaderId] = useState("");
  const [category, setCategory] = useState<CategoryType[]>([]);
  const [dataFetched, setDataFetched] = useState(false);
  const [page, setPage] = useState<number>(() =>
    Number(getKey(listAssignProductsToVendors.page) || 1),
  );
  const [pageLimit, setPageLimit] = useState<number>(() =>
    Number(getKey(listAssignProductsToVendors.limit) || PAGE_LIMIT),
  );
  const [selectedVendorId, setSelectedVendorId] = useState<string>(() => {
    const saved = getKey(listAssignProductsToVendors.selectedVendor);
    if (!saved) return "";
    if (
      typeof saved === "string" &&
      saved.startsWith('"') &&
      saved.endsWith('"')
    ) {
      try {
        return JSON.parse(saved);
      } catch {
        return saved;
      }
    }
    return saved.toString();
  });
  const [search, setSearch] = useState<string>(
    () => getKey(listAssignProductsToVendors.search)?.toString() || "",
  );
  const [primaryCategories, setPrimaryCategories] = useState<CategoryType[]>(
    [],
  );
  const [subCategories, setSubCategories] = useState<CategoryType[]>([]);
  const [groupCategories, setGroupCategories] = useState<CategoryType[]>([]);
  const [brands, setBrands] = useState<any[]>([]);

  // Selection state for bulk assign/unassign
  const [selected, setSelected] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<any>([]);
  const [selectedData, setSelectedData] = useState<any>([]);
  const [selectLoading, setSelectLoading] = useState<boolean>(false);

  const [selectedPrimary, setSelectedPrimary] = useState<CategoryType | null>(
    null,
  );
  const [selectedSub, setSelectedSub] = useState<CategoryType | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<CategoryType | null>(null);
  const [selectedBrand, setSelectedBrand] = useState<any>(null);

  useEffect(() => {
    (async () => {
      await Promise.all([
        fetchVendorsList(),
        fetchCategory(depth),
        fetchBrands(),
      ]);
    })();
  }, []);

  useEffect(() => {
    if (category && category.length) {
      setPrimaryCategories(category);

      const savedPrimaryId = getKey(
        listAssignProductsToVendors.filterPrimary,
      )?.toString();
      const savedSubId = getKey(
        listAssignProductsToVendors.filterSub,
      )?.toString();
      const savedGroupId = getKey(
        listAssignProductsToVendors.filterGroup,
      )?.toString();
      const savedBrandId = getKey(
        listAssignProductsToVendors.filterBrand,
      )?.toString();

      const firstPrimary: any = savedPrimaryId
        ? category.find((c: any) => c._id === savedPrimaryId) || category[0]
        : category[0];

      setSelectedPrimary(firstPrimary);
      const subs = firstPrimary?.categories || [];
      setSubCategories(subs);

      const matchedSub = savedSubId
        ? subs.find((s: any) => s._id === savedSubId)
        : null;
      setSelectedSub(matchedSub);

      const groups = matchedSub?.categories || [];
      setGroupCategories(groups);

      const matchedGroup = savedGroupId
        ? groups.find((g: any) => g._id === savedGroupId)
        : null;
      setSelectedGroup(matchedGroup);

      // set selectedBrand if stored
      if (savedBrandId && savedBrandId !== "") {
        setSelectedBrand({ value: savedBrandId, title: "", label: "" });
      }

      fetchProducts(
        page,
        pageLimit,
        search,
        firstPrimary?._id,
        matchedSub?._id || "",
        matchedGroup?._id || "",
        savedBrandId || "",
        selectedVendorId || "",
      );
    }
  }, [category]);

  useEffect(() => {
    if (state?.id && vendorsList.length > 0 && category.length > 0) {
      const selectedVendor = vendorsList.find((v: any) => v?._id === state.id);
      if (selectedVendor) {
        handleVendorChange({ value: selectedVendor._id });
      }
    }
  }, [state?.id, vendorsList.length, category.length]);

  const fetchCategory = async (categoriesDepth: number) => {
    setLoading(true);
    let params: any = {
      sortKey: "title",
      sortOrder: 1,
      categoriesDepth: categoriesDepth,
    };
    let apiService = new APICallService(
      master.categoryList,
      params,
      "",
      "",
      "",
      "",
      Categories,
    );
    let response = await apiService.callAPI();
    if (response?.records) {
      setCategory(response.records);
    }
    setDataFetched(true);
    setLoading(false);
  };

  const fetchBrands = async () => {
    setLoading(true);
    let params: any = {
      sortKey: "title",
      sortOrder: 1,
    };
    let apiService = new APICallService(
      master.listBrands,
      params,
      "",
      "",
      "",
      "",
      Brand,
    );
    let response = await apiService.callAPI();
    if (response.records) {
      setBrands(response.records);
    }
    setDataFetched(true);
    setLoading(false);
  };

  const fetchVendorsList = async () => {
    setFetchLoading(true);
    let params: any = {
      sortKey: "name",
      sortOrder: 1,
      needCount: true,
      "status[0]": 4,
    };
    const apiCallService = new APICallService(
      vendor.listVendor,
      params,
      "",
      "",
      "",
      "",
    );
    const response = await apiCallService.callAPI();
    if (response && response?.records) {
      // let tempData = response?.records?.map((item: any) => {
      //   return {
      //     value: item?._id,
      //     label: item?.name,
      //     title: item?.name,
      //   };
      // });
      // setVendorList(tempData);
      setVendorList(response?.records);
    }
    setFetchLoading(false);
  };

  // const fetchProducts = async (page: number, limit: number) => {
  //   setLoading(true);
  //   let params: any = {
  //     pageNo: page,
  //     limit: limit,
  //     sortKey: "title",
  //     sortOrder: 1,
  //     needCount: true,
  //     status: "2",
  //   };
  //   const apiCallService = new APICallService(
  //     manageProduct.listProduct,
  //     params,
  //     "",
  //     "",
  //     "",
  //     ""
  //   );
  //   const response = await apiCallService.callAPI();
  //   if (response) {
  //     setProducts(response?.records || []);
  //     if (page == 1) {
  //       setTotalRecords(response?.total || 0);
  //     }
  //   }
  //   setLoading(false);
  // };

  const fetchProducts = async (
    page: number,
    limit: number,
    searchTerm?: string,
    selectedPrimaryId?: string,
    selectedSubId?: string,
    selectedGroupId?: string,
    selectedBrandId?: string,
    currentVendorId?: string,
  ) => {
    setLoading(true);
    let params: any = {
      pageNo: page,
      limit: limit,
      sortKey: "title",
      sortOrder: 1,
      needCount: true,
      searchTerm: searchTerm || "",
      state: AllProduct,
      status: ApprovedProduct,
    };
    if (selectedPrimaryId) {
      params["categories[0]"] = selectedPrimaryId;
    }
    if (selectedSubId) {
      params["subCategories[0]"] = selectedSubId;
    }
    if (selectedGroupId) {
      params["groupCategories[0]"] = selectedGroupId;
    }
    if (selectedBrandId) {
      params["brands[0]"] = selectedBrandId;
    }
    const apiCallService = new APICallService(
      manageProduct.listProduct,
      params,
      "",
      "",
      "",
      "",
    );
    const response = await apiCallService.callAPI();
    if (response) {
      setProducts(response?.records || []);
      setTotalRecords(response?.total || 0);

      // Logic to pre-select assigned products based on the vendor
      const vendorToCheck = currentVendorId || selectedVendorId;
      if (vendorToCheck) {
        let ids: string[] = [];
        let data: any[] = [];
        (response?.records || []).forEach((p: any) => {
          let allVariantsSelected = true;
          let hasVariants = false;

          // Check if variants are assigned
          if (Array.isArray(p.variants) && p.variants.length > 0) {
            const validVariants = p.variants.filter(
              (v: any) => v?.variant?._id,
            );
            if (validVariants.length > 0) {
              hasVariants = true;
              validVariants.forEach((variantObj: any) => {
                const isAssigned = variantObj.variant.businesses?.some(
                  (b: any) => b.reference === vendorToCheck,
                );
                if (isAssigned) {
                  const variantKey = `v:${variantObj.variant._id}`;
                  ids.push(variantKey);
                  data.push({
                    type: "variant",
                    product: p,
                    variant: variantObj,
                  });
                } else {
                  allVariantsSelected = false;
                }
              });
            } else {
              allVariantsSelected = false;
            }
          } else {
            allVariantsSelected = false;
          }

          // If all valid variants are assigned, select the product too
          if (hasVariants && allVariantsSelected) {
            const productKey = `p:${p?._id}`;
            ids.push(productKey);
            data.push({ type: "product", product: p });
          }
        });
        setSelectedId(ids);
        setSelectedData(data);
        setSelected(
          ids.filter((k) => k.startsWith("p:")).length ===
            (response?.records?.length || 0) && response?.records?.length > 0,
        );
      } else {
        // Reset selection if no vendor is selected
        setSelectedId([]);
        setSelectedData([]);
        setSelected(false);
      }
    }
    setLoading(false);
  };

  const debounce = useDebounce(fetchProducts, 300);

  const handleSearch = async (input: string) => {
    setSearch(input);
    setPage(1);
    setKey(listAssignProductsToVendors.search, input);

    await debounce(
      1,
      pageLimit,
      input,
      selectedPrimary?._id,
      selectedSub?._id,
      selectedGroup?._id,
      selectedBrand?._id,
    );
  };

  const handleCurrentPage = async (val: number) => {
    if (val === page || val.toString() === "...") return;
    setPage(val);
    setKey(listAssignProductsToVendors.page, val);
    await fetchProducts(
      val,
      pageLimit,
      search,
      selectedPrimary?._id,
      selectedSub?._id,
      selectedGroup?._id,
      selectedBrand?._id,
    );
    // await fetchProducts(val, pageLimit);
  };

  const handleNextPage = async (val: number) => {
    setPage(val + 1);
    setKey(listAssignProductsToVendors.page, val + 1);
    await fetchProducts(
      val + 1,
      pageLimit,
      search,
      selectedPrimary?._id,
      selectedSub?._id,
      selectedGroup?._id,
      selectedBrand?._id,
    );
    // await fetchProducts(val + 1, pageLimit);
  };

  const handlePreviousPage = async (val: number) => {
    setPage(val - 1);
    setKey(listAssignProductsToVendors.page, val - 1);
    await fetchProducts(
      val - 1,
      pageLimit,
      search,
      selectedPrimary?._id,
      selectedSub?._id,
      selectedGroup?._id,
      selectedBrand?._id,
    );
    // await fetchProducts(val - 1, pageLimit);
  };

  const handlePageLimit = async (event: any) => {
    setPage(1);
    setPageLimit(+event.target.value);
    setKey(listAssignProductsToVendors.limit, +event.target.value);
    await fetchProducts(
      1,
      event.target.value,
      search,
      selectedPrimary?._id,
      selectedSub?._id,
      selectedGroup?._id,
      selectedBrand?._id,
    );
    // await fetchProducts(1, event.target.value);
  };

  const handleVariationShow = (variantionData: any, index: number) => {
    let temp: any = [...products];
    temp[index].showVariant = !temp[index].showVariant;
    setProducts(temp);
  };

  const checkIsAssign = (vendorId: any, data: any) => {
    let index = data?.findIndex((item: any) => item?.reference == vendorId);
    return index != -1;
  };

  const hanldeAssignUnassign = async (
    productId: any | any[],
    vendorId: any,
    isAssign: boolean,
    loaderKey?: string,
  ) => {
    const normalizeId = (id: any) => {
      if (id == null || id === "") return "";
      let value = typeof id === "string" ? id : `${id}`;
      if (value.startsWith('"') && value.endsWith('"')) {
        try {
          value = JSON.parse(value);
        } catch {
          // keep as-is
        }
      }
      return value;
    };
    const ids = (Array.isArray(productId) ? productId : [productId])
      .map(normalizeId)
      .filter(Boolean);
    const cleanVendorId = normalizeId(vendorId);
    if (!cleanVendorId) {
      return error(assignToVendorToast.selectVendor);
    }
    setLoaderId(loaderKey ?? (Array.isArray(productId) ? ids[0] : productId));
    let params = {
      productIds: ids,
      vendorId: cleanVendorId,
    };
    let endPoint = isAssign
      ? vendor.unassignProductToVendor
      : vendor.assignProductToVendor;
    const apiCallService = new APICallService(endPoint, params, "", "", "", "");
    const response = await apiCallService.callAPI();
    if (response) {
      const msg = isAssign
        ? assignToVendorToast.vendorUnAssigned
        : assignToVendorToast.vendorAssigned;
      success(msg);

      setProducts((prevProducts: any[]) => {
        return prevProducts.map((product) => {
          if (
            ids.includes(product._id) ||
            (product.variants &&
              product.variants.some((v: any) => ids.includes(v?.variant?._id)))
          ) {
            const updatedVariants = product.variants.map((variantObj: any) => {
              if (ids.includes(variantObj?.variant?._id)) {
                let businesses = Array.isArray(variantObj.variant.businesses)
                  ? [...variantObj.variant.businesses]
                  : [];
                if (isAssign) {
                  businesses = businesses.filter(
                    (b: any) => normalizeId(b.reference) !== cleanVendorId,
                  );
                } else {
                  if (
                    !businesses.some(
                      (b: any) => normalizeId(b.reference) === cleanVendorId,
                    )
                  ) {
                    businesses.push({ reference: cleanVendorId });
                  }
                }
                return {
                  ...variantObj,
                  variant: {
                    ...variantObj.variant,
                    businesses,
                  },
                };
              }
              return variantObj;
            });
            return {
              ...product,
              variants: updatedVariants,
            };
          }
          return product;
        });
      });
    }
    setLoaderId("");
  };

  const buildIdsForProduct = (product: any): string[] => {
    const hasVariants =
      product?.variants?.length &&
      product?.variants[0]?.variant?.["variantType"]?.[0] != undefined;
    if (hasVariants) {
      const variantIds = (product?.variants || [])
        .map((v: any) => v?.variant?._id)
        .filter((id: any) => id);
      return [product?._id, ...variantIds].filter(Boolean);
    }
    const singleVariantId = product?.variants?.[0]?.variant?._id;
    return [product?._id, singleVariantId].filter(Boolean);
  };

  const buildIdsForVariant = (product: any, variant: any): string[] => {
    return [product?._id, variant?.variant?._id].filter(Boolean);
  };

  const handleCheckedProduct = (product: any) => {
    const productKey = `p:${product?._id}`;
    const variantKeys = (
      Array.isArray(product.variants) ? product.variants : []
    )
      .map((v: any) => v?.variant?._id)
      .filter((id: any) => id)
      .map((id: any) => `v:${id}`);
    let ids = [...selectedId];
    let data = [...selectedData];
    if (ids.includes(productKey)) {
      ids = ids.filter((k) => k !== productKey && !variantKeys.includes(k));
      data = data.filter((d: any) => {
        if (d.type === "product" && d.product?._id === product?._id)
          return false;
        if (d.type === "variant" && d.product?._id === product?._id)
          return false;
        return true;
      });
    } else {
      ids.push(productKey);
      data.push({ type: "product", product });
      variantKeys.forEach((vk: string, idx: number) => {
        if (!ids.includes(vk)) {
          ids.push(vk);
          data.push({
            type: "variant",
            product,
            variant: product.variants[idx],
          });
        }
      });
    }
    setSelectedId(ids);
    setSelectedData(data);
    setSelected(
      ids.filter((k) => k.startsWith("p:")).length === (products?.length || 0),
    );
  };

  const handleCheckedVariant = (product: any, variant: any) => {
    const key = `v:${variant?.variant?._id}`;
    let ids = [...selectedId];
    let data = [...selectedData];
    if (ids.includes(key)) {
      ids = ids.filter((k) => k !== key);
      data = data.filter(
        (d: any) =>
          !(
            d.type === "variant" &&
            d.variant?.variant?._id === variant?.variant?._id
          ),
      );
    } else {
      ids.push(key);
      data.push({ type: "variant", product, variant });
    }

    const productKey = `p:${product?._id}`;
    const allVariantKeys = (
      Array.isArray(product.variants) ? product.variants : []
    )
      .map((v: any) => v?.variant?._id)
      .filter((id: any) => id)
      .map((id: any) => `v:${id}`);

    const allSelected =
      allVariantKeys.length > 0 &&
      allVariantKeys.every((vk: string) => ids.includes(vk));

    if (allSelected) {
      if (!ids.includes(productKey)) {
        ids.push(productKey);
        data.push({ type: "product", product });
      }
    } else {
      if (ids.includes(productKey)) {
        ids = ids.filter((k) => k !== productKey);
        data = data.filter(
          (d: any) =>
            !(d.type === "product" && d.product?._id === product?._id),
        );
      }
    }

    setSelectedId(ids);
    setSelectedData(data);
    setSelected(
      ids.filter((k: any) => k.startsWith("p:")).length ===
        (products?.length || 0) && products?.length > 0,
    );
  };

  const handleSelectAll = async () => {
    if (!selectedVendorId) {
      return error(assignToVendorToast.selectVendor);
    }
    if (selected) {
      setSelectLoading(true);
      setSelectedData([]);
      setSelectedId([]);
      setSelected(false);
      setSelectLoading(false);
    } else {
      setSelectLoading(true);
      const ids: string[] = [];
      const data: any[] = [];
      (products || []).forEach((p: any) => {
        const productKey = `p:${p?._id}`;
        ids.push(productKey);
        data.push({ type: "product", product: p });
        if (Array.isArray(p.variants)) {
          p.variants.forEach((variantObj: any) => {
            if (variantObj?.variant?._id) {
              const variantKey = `v:${variantObj.variant._id}`;
              ids.push(variantKey);
              data.push({ type: "variant", product: p, variant: variantObj });
            }
          });
        }
      });
      setSelectedId(ids);
      setSelectedData(data);
      setSelected(true);
      setSelectLoading(false);
    }
  };

  const handleVendorChange = async (opt: any) => {
    const vendorId: string = opt?.value ?? "";
    setSelectedVendorId(vendorId);
    setKey(listAssignProductsToVendors.selectedVendor, vendorId, false);

    // If vendor is cleared, restore full categories and default select first
    if (!vendorId) {
      const allPrimary = category ?? [];
      setPrimaryCategories(allPrimary);
      const first: CategoryType | null = allPrimary[0] ?? null;
      setSelectedPrimary(first);
      setSubCategories(first?.categories ?? []);
      setSelectedSub(null);
      setGroupCategories([]);
      setSelectedGroup(null);
      setPage(1);
      await fetchProducts(
        1,
        pageLimit,
        search,
        first?._id,
        "",
        "",
        selectedBrand?._id,
      );
      return;
    }

    // Find the selected vendor directly from vendorsList (response.records)
    const vendorRec = vendorsList.find((v: any) => v?._id === vendorId);
    const vendorCats = vendorRec?.categories ?? [];
    const vendorCatIds = vendorCats
      .map((c: any) => c?.reference?._id ?? "")
      .filter(Boolean);

    // Filter primary categories based on vendor's category ids; fallback to all if empty
    let filteredPrimary: CategoryType[] = (category ?? []).filter((c: any) =>
      vendorCatIds.length ? vendorCatIds.includes(c?._id) : true,
    );
    if (!filteredPrimary.length) filteredPrimary = category ?? [];

    setPrimaryCategories(filteredPrimary);
    const firstPrimary: CategoryType | null = filteredPrimary[0] ?? null;
    setSelectedPrimary(firstPrimary);
    setSubCategories(firstPrimary?.categories ?? []);
    setSelectedSub(null);
    setGroupCategories([]);
    setSelectedGroup(null);

    // Refresh products with new primary category filter
    setPage(1);
    await fetchProducts(
      1,
      pageLimit,
      search,
      firstPrimary?._id,
      "",
      "",
      selectedBrand?._id,
      vendorId,
    );
  };

  const handlePrimaryChange = async (option: any) => {
    const selected = primaryCategories.find(
      (item) => item._id === option.value,
    );
    setSelectedPrimary(selected || null);
    setSubCategories(selected?.categories || []);
    setSelectedSub(null);
    setGroupCategories([]);
    setSelectedGroup(null);
    setKey(
      listAssignProductsToVendors.filterPrimary,
      option ? option.value : "",
    );
    await fetchProducts(
      page,
      pageLimit,
      search,
      selected?._id,
      "",
      "",
      selectedBrand?._id,
    );
  };

  const handleSubChange = async (option: any) => {
    const selected = subCategories.find(
      (item) => item._id === (option ? option.value : ""),
    );
    setSelectedSub(selected || null);
    setGroupCategories(selected?.categories || []);
    setSelectedGroup(null);
    setKey(listAssignProductsToVendors.filterSub, option ? option.value : "");
    await fetchProducts(
      page,
      pageLimit,
      search,
      selectedPrimary?._id,
      selected ? selected._id : "",
      "",
      selectedBrand?._id,
    );
  };

  const handleGroupChange = async (option: any) => {
    const selected = groupCategories.find(
      (item) => item._id === (option ? option.value : ""),
    );
    setSelectedGroup(selected || null);
    setKey(listAssignProductsToVendors.filterGroup, option ? option.value : "");
    await fetchProducts(
      page,
      pageLimit,
      search,
      selectedPrimary?._id,
      selectedSub?._id,
      option ? option.value : "",
      selectedBrand?._id,
    );
  };

  return (
    <div className="p-5">
      <Row className="g-5">
        <Col xs={12}>
          <h1 className="fs-22 fw-bolder">Assign Products to Suppliers</h1>
        </Col>

        <Col xs={12}>
          <Card className="bg-light border">
            <Card.Body className="px-7 py-6">
              <Row className="align-items-center">
                <Col xs={12} sm={12} md={6} lg={6}>
                  <div className="w-100">
                    <CustomSelect
                      backgroundColor="#ffffff"
                      placeholder="Select supplier"
                      isSearchable={true}
                      isClearable={true}
                      isLoading={fetchLoading}
                      // options={vendorsList}
                      options={vendorsList.map((val: any) => ({
                        label: (
                          <>
                            <div className="symbol symbol-30px border me-3">
                              <img
                                src={val.image}
                                className="object-fit-contain"
                                alt=""
                              />
                            </div>
                            <span className="fs-16 fw-600 text-black mb-0">
                              {val.ownerName}
                            </span>
                          </>
                        ),
                        value: val._id,
                        title: val.ownerName,
                      }))}
                      width={"100%"}
                      minWidth={"100%"}
                      // value={
                      //   vendorOptions.find(
                      //     (o) => o.value === selectedVendorId
                      //   ) || null
                      // }
                      value={
                        selectedVendorId
                          ? vendorsList
                              .filter((v: any) => v._id === selectedVendorId)
                              .map((val: any) => ({
                                label: (
                                  <>
                                    <div className="symbol symbol-30px border me-3">
                                      <img
                                        src={val.image}
                                        className="object-fit-contain"
                                        alt=""
                                      />
                                    </div>
                                    <span className="fs-16 fw-600 text-black mb-0">
                                      {val.ownerName}
                                    </span>
                                  </>
                                ),
                                value: val._id,
                                title: val.ownerName,
                              }))[0] || null
                          : null
                      }
                      onChange={handleVendorChange}
                      // onChange={(opt: any) =>
                      //   setSelectedVendorId(opt ? opt.value : "")
                      // }
                    />
                  </div>
                </Col>
                <Col xs={12} sm={12} md={6} lg={6}>
                  <div className="w-100">
                    <CustomSearchInput
                      placeholder="Search products..."
                      defaultValue={search}
                      onSearch={(value) => handleSearch(value)}
                      className="bg-white"
                    />
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12}>
          <Card className="bg-light border">
            <Card.Body className="px-7 py-6">
              <Row className="g-4">
                <Col xs={12} sm={12} md={6} lg={4}>
                  <div className="w-100">
                    <CustomSelect
                      backgroundColor="#ffffff"
                      placeholder="Select Primary Category"
                      width={"100%"}
                      minWidth={"100%"}
                      options={primaryCategories.map((val: any) => ({
                        label: (
                          <>
                            <div className="symbol symbol-30px border me-3">
                              <img
                                src={val.image}
                                className="object-fit-contain"
                                alt=""
                              />
                            </div>
                            <span className="fs-16 fw-600 text-black">
                              {val.title}
                            </span>
                          </>
                        ),
                        value: val._id,
                        title: val.title,
                      }))}
                      value={
                        selectedPrimary
                          ? {
                              value: selectedPrimary._id,
                              title: selectedPrimary.title,
                              label: (
                                <>
                                  <div className="symbol symbol-30px border me-3">
                                    <img
                                      src={selectedPrimary.image}
                                      className="object-fit-contain"
                                      alt=""
                                    />
                                  </div>
                                  <span className="fs-16 fw-600 text-black">
                                    {selectedPrimary.title}
                                  </span>
                                </>
                              ),
                            }
                          : null
                      }
                      onChange={handlePrimaryChange}
                      isClearable={false}
                      isLoading={!dataFetched}
                    />
                  </div>
                </Col>

                <Col xs={12} sm={12} md={6} lg={4}>
                  <div className="w-100">
                    <CustomSelect
                      backgroundColor="#ffffff"
                      placeholder="Select Sub Category"
                      width={"100%"}
                      minWidth={"100%"}
                      options={subCategories.map((val: any) => ({
                        label: (
                          <>
                            <div className="symbol symbol-30px border me-3">
                              <img
                                src={val.image}
                                className="object-fit-contain"
                                alt=""
                              />
                            </div>
                            <span className="fs-16 fw-600 text-black">
                              {val.title}
                            </span>
                          </>
                        ),
                        value: val._id,
                        title: val.title,
                      }))}
                      value={
                        selectedSub
                          ? {
                              value: selectedSub._id,
                              title: selectedSub.title,
                              label: (
                                <>
                                  <div className="symbol symbol-30px border me-3">
                                    <img
                                      src={selectedSub.image}
                                      className="object-fit-contain"
                                      alt=""
                                    />
                                  </div>
                                  <span className="fs-16 fw-600 text-black">
                                    {selectedSub.title}
                                  </span>
                                </>
                              ),
                            }
                          : null
                      }
                      onChange={handleSubChange}
                      isClearable={true}
                      isDisabled={!subCategories.length}
                      isLoading={!dataFetched}
                    />
                  </div>
                </Col>

                <Col xs={12} sm={12} md={6} lg={4}>
                  <div className="w-100">
                    <CustomSelect
                      backgroundColor="#ffffff"
                      placeholder="Select Group"
                      width={"100%"}
                      minWidth={"100%"}
                      options={groupCategories.map((val: any) => ({
                        label: (
                          <>
                            <span className="fs-16 fw-600 text-black">
                              {val.title}
                            </span>
                          </>
                        ),
                        value: val._id,
                        title: val.title,
                      }))}
                      value={
                        selectedGroup
                          ? {
                              value: selectedGroup._id,
                              title: selectedGroup.title,
                              label: (
                                <>
                                  <span className="fs-16 fw-600 text-black">
                                    {selectedGroup.title}
                                  </span>
                                </>
                              ),
                            }
                          : null
                      }
                      onChange={handleGroupChange}
                      isClearable={true}
                      isDisabled={!groupCategories.length}
                      isLoading={!dataFetched}
                    />
                  </div>
                </Col>

                {/* <Col xs={12} sm={12} md={6} lg={3}>
                  <div className="w-100">
                    <CustomSelect
                      backgroundColor={'#f9f9f9'}
                      placeholder="Select Brand"
                      value={selectedBrand}
                      onChange={handleBrandChange}
                      width={'100%'}
                      minWidth={'100%'}
                      options={brands.map((val: any) => ({
                        label: (
                          <span className="fs-16 fw-600 text-black">{val.title}</span>
                        ),
                        value: val._id,
                        title: val.title,
                      }))}
                      isLoading={!dataFetched}
                      isClearable={true}
                    />
                  </div>
                </Col> */}
              </Row>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12}>
          <Card className="border border-r10px">
            <Card.Body className="p-0">
              <div className="table-responsive">
                <table className="table table-rounded table-row-bordered align-middle gs-7 gy-4">
                  <thead>
                    <tr className="fw-bold fs-16 fw-600 text-dark border-bottom h-70px align-middle">
                      <th className="min-w-400px ">
                        <div className="form-check form-check-custom form-check-solid d-flex align-items-center">
                          {selectedVendorId ? (
                            <input
                              className="form-check-input h-30px w-30px me-3"
                              type="checkbox"
                              value="1"
                              checked={
                                selectLoading ||
                                (selected &&
                                  selectedId.filter((k: any) =>
                                    k.startsWith("p:"),
                                  ).length === (products?.length || 0))
                              }
                              onChange={handleSelectAll}
                              disabled={selectLoading || loading}
                            />
                          ) : (
                            <></>
                          )}
                          Product name
                        </div>
                      </th>
                      <th className="min-w-125px">Ref Key</th>
                      <th className="min-w-100px text-end">
                        {selectedData.length > 0 && (
                          <>
                            <Button
                              size="sm"
                              className="uw-btn uw-btn-primary uw-btn-sm min-h-32px px-3 me-2"
                              disabled={selectLoading || !selectedVendorId}
                              onClick={() => {
                                const allIds = Array.from(
                                  new Set(
                                    selectedData.flatMap((item: any) =>
                                      item.type === "product"
                                        ? buildIdsForProduct(item.product)
                                        : buildIdsForVariant(
                                            item.product,
                                            item.variant,
                                          ),
                                    ),
                                  ),
                                );
                                hanldeAssignUnassign(
                                  allIds,
                                  selectedVendorId,
                                  false,
                                  "bulk",
                                );
                              }}
                            >
                              Assign Selected
                              {selectLoading && (
                                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="secondary"
                              className="uw-btn uw-btn-light uw-btn-sm min-h-32px px-3"
                              disabled={selectLoading || !selectedVendorId}
                              onClick={() => {
                                const allIds = Array.from(
                                  new Set(
                                    selectedData.flatMap((item: any) =>
                                      item.type === "product"
                                        ? buildIdsForProduct(item.product)
                                        : buildIdsForVariant(
                                            item.product,
                                            item.variant,
                                          ),
                                    ),
                                  ),
                                );
                                hanldeAssignUnassign(
                                  allIds,
                                  selectedVendorId,
                                  true,
                                  "bulk",
                                );
                              }}
                            >
                              Unassign Selected
                              {selectLoading && (
                                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                              )}
                            </Button>
                          </>
                        )}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <>
                      {loading ? (
                        <>
                          <tr>
                            <td colSpan={3}>
                              <div className="w-100 fs-15 fw-bold d-flex justify-content-center">
                                <Loader loading={loading} />
                              </div>
                            </td>
                          </tr>
                        </>
                      ) : (
                        <>
                          {products.length ? (
                            <>
                              {products.map(
                                (productVal: any, index: number) => {
                                  return (
                                    <React.Fragment
                                      key={productVal?._id || index}
                                    >
                                      <tr>
                                        <td>
                                          <div className="d-flex align-items-center">
                                            {selectedVendorId ? (
                                              <div className="form-check form-check-custom form-check-solid me-3">
                                                <input
                                                  className="form-check-input h-30px w-30px"
                                                  type="checkbox"
                                                  value="1"
                                                  checked={selectedId.includes(
                                                    `p:${productVal?._id}`,
                                                  )}
                                                  onChange={() =>
                                                    handleCheckedProduct(
                                                      productVal,
                                                    )
                                                  }
                                                  disabled={
                                                    selectLoading || loading
                                                  }
                                                />
                                              </div>
                                            ) : (
                                              <></>
                                            )}
                                            <div className="symbol symbol-50px me-5">
                                              <img
                                                src={
                                                  productVal.variants[0]
                                                    ?.variant?.media[0]?.url ||
                                                  ""
                                                }
                                                className="object-fit-contain"
                                                alt=""
                                              />
                                            </div>
                                            <div className="fs-15 fw-600">
                                              {productVal.title.replace(
                                                /\s*\)\s*/g,
                                                ")",
                                              )}
                                            </div>
                                          </div>
                                        </td>
                                        <td className="text-start justify-content-between align-items-center">
                                          {productVal.variants.length &&
                                          productVal.variants[0].variant[
                                            "variantType"
                                          ][0] != undefined ? (
                                            <>-</>
                                          ) : (
                                            <>
                                              {productVal.variants[0]?.variant
                                                ?.refKey || "-"}
                                            </>
                                          )}
                                        </td>

                                        <td className="text-end">
                                          <div className="d-flex flex-nowrap justify-content-end align-items-center">
                                            {productVal.variants.length &&
                                            productVal.variants[0].variant[
                                              "variantType"
                                            ][0] != undefined ? (
                                              <>
                                                <span
                                                  className="fs-16 fw-600 text-gray text-hover-primary cursor-pointer me-3"
                                                  onClick={() => {
                                                    handleVariationShow(
                                                      products.variants,
                                                      index,
                                                    );
                                                  }}
                                                >
                                                  {productVal.showVariant
                                                    ? "Hide"
                                                    : "View"}{" "}
                                                  {productVal.variants.length}{" "}
                                                  variations
                                                </span>
                                                <Button
                                                  variant="primary"
                                                  className="uw-btn uw-btn-primary btn-primary fs-14 fw-600 me-3"
                                                  disabled={
                                                    loaderId === productVal?._id
                                                  }
                                                  onClick={() => {
                                                    const allAssigned = (
                                                      productVal?.variants || []
                                                    ).every((v: any) =>
                                                      checkIsAssign(
                                                        selectedVendorId,
                                                        v?.variant
                                                          ?.businesses || [],
                                                      ),
                                                    );
                                                    const variantIds = [
                                                      productVal._id,
                                                      ...(
                                                        productVal?.variants ||
                                                        []
                                                      )
                                                        .map(
                                                          (v: any) =>
                                                            v?.variant?._id,
                                                        )
                                                        .filter(
                                                          (id: any) => id,
                                                        ),
                                                    ];
                                                    hanldeAssignUnassign(
                                                      variantIds,
                                                      selectedVendorId,
                                                      allAssigned,
                                                      productVal?._id,
                                                    );
                                                  }}
                                                >
                                                  {loaderId ===
                                                  productVal?._id ? (
                                                    <>
                                                      <span
                                                        className="indicator-progress fs-16 fw-bold"
                                                        style={{
                                                          display: "block",
                                                        }}
                                                      >
                                                        {String.PleaseWait}
                                                        <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                                                      </span>
                                                    </>
                                                  ) : (
                                                      productVal?.variants || []
                                                    ).every((v: any) =>
                                                      checkIsAssign(
                                                        selectedVendorId,
                                                        v?.variant
                                                          ?.businesses || [],
                                                      ),
                                                    ) ? (
                                                    "Unassign All Variant"
                                                  ) : (
                                                    "Assign All Variants"
                                                  )}
                                                </Button>
                                              </>
                                            ) : (
                                              <>
                                                <Button
                                                  variant="primary"
                                                  className="uw-btn uw-btn-primary btn-primary fs-14 fw-600 me-5"
                                                  disabled={
                                                    loaderId ==
                                                    productVal?.variants[0]
                                                      ?.variant?._id
                                                  }
                                                  onClick={() => {
                                                    const ids = [
                                                      productVal._id,
                                                      productVal?.variants[0]
                                                        ?.variant?._id,
                                                    ].filter((id: any) => id);
                                                    hanldeAssignUnassign(
                                                      ids,
                                                      selectedVendorId,
                                                      checkIsAssign(
                                                        selectedVendorId,
                                                        productVal.variants[0]
                                                          ?.variant
                                                          ?.businesses || [],
                                                      ),
                                                    );
                                                  }}
                                                >
                                                  {loaderId ==
                                                  productVal?.variants[0]
                                                    ?.variant?._id ? (
                                                    <>
                                                      <span
                                                        className="indicator-progress fs-16 fw-bold"
                                                        style={{
                                                          display: "block",
                                                        }}
                                                      >
                                                        {String.PleaseWait}
                                                        <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                                                      </span>
                                                    </>
                                                  ) : checkIsAssign(
                                                      selectedVendorId,
                                                      productVal.variants[0]
                                                        ?.variant?.businesses ||
                                                        [],
                                                    ) ? (
                                                    "Unassign"
                                                  ) : (
                                                    "Assign Product"
                                                  )}
                                                </Button>
                                              </>
                                            )}
                                          </div>
                                        </td>
                                      </tr>
                                      {productVal.showVariant ? (
                                        productVal.variants.map(
                                          (
                                            variantVal: any,
                                            variantIndex: number,
                                          ) => {
                                            return (
                                              <tr
                                                key={
                                                  variantVal?.variant?._id ||
                                                  `variant-${variantIndex}`
                                                }
                                                className="table-active ms-6"
                                              >
                                                <td className="ps-15">
                                                  <div className="d-flex align-items-center">
                                                    {selectedVendorId ? (
                                                      <div className="form-check form-check-custom form-check-solid me-3">
                                                        <input
                                                          className="form-check-input h-30px w-30px"
                                                          type="checkbox"
                                                          value="1"
                                                          checked={selectedId.includes(
                                                            `v:${variantVal?.variant?._id}`,
                                                          )}
                                                          onChange={() =>
                                                            handleCheckedVariant(
                                                              productVal,
                                                              variantVal,
                                                            )
                                                          }
                                                          disabled={
                                                            selectLoading ||
                                                            loading
                                                          }
                                                        />
                                                      </div>
                                                    ) : (
                                                      <></>
                                                    )}
                                                    <div className="symbol symbol-50px border me-5">
                                                      <img
                                                        src={
                                                          variantVal?.variant
                                                            ?.media[0]?.url ||
                                                          ""
                                                        }
                                                        className="object-fit-contain"
                                                        alt=""
                                                      />
                                                    </div>
                                                    <div className="fs-15 fw-600">
                                                      {variantVal.variant.title.replace(
                                                        /\s*\)\s*/g,
                                                        ")",
                                                      )}
                                                    </div>
                                                  </div>
                                                </td>
                                                <td className="text-start justify-content-between align-items-center">
                                                  {variantVal.variant.refKey ||
                                                    "-"}
                                                </td>
                                                <td className="text-end">
                                                  <div className="d-flex flex-nowrap justify-content-end align-items-center">
                                                    <Button
                                                      variant="primary"
                                                      className="uw-btn uw-btn-primary me-3 primary fs-14 fw-600"
                                                      style={{
                                                        whiteSpace: "nowrap",
                                                      }}
                                                      disabled={
                                                        loaderId ==
                                                        variantVal?.variant?._id
                                                      }
                                                      onClick={() => {
                                                        const ids = [
                                                          productVal._id,
                                                          variantVal?.variant
                                                            ?._id,
                                                        ].filter(
                                                          (id: any) => id,
                                                        );
                                                        hanldeAssignUnassign(
                                                          ids,
                                                          selectedVendorId,
                                                          checkIsAssign(
                                                            selectedVendorId,
                                                            variantVal?.variant
                                                              ?.businesses ||
                                                              [],
                                                          ),
                                                          variantVal?.variant
                                                            ?._id,
                                                        );
                                                      }}
                                                    >
                                                      {loaderId ==
                                                      variantVal?.variant
                                                        ?._id ? (
                                                        <>
                                                          <span
                                                            className="indicator-progress fs-16 fw-bold"
                                                            style={{
                                                              display: "block",
                                                            }}
                                                          >
                                                            {String.PleaseWait}
                                                            <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                                                          </span>
                                                        </>
                                                      ) : checkIsAssign(
                                                          selectedVendorId,
                                                          variantVal?.variant
                                                            ?.businesses || [],
                                                        ) ? (
                                                        "Unassign"
                                                      ) : (
                                                        "Assign Product"
                                                      )}
                                                    </Button>
                                                  </div>
                                                </td>
                                              </tr>
                                            );
                                          },
                                        )
                                      ) : (
                                        <></>
                                      )}
                                    </React.Fragment>
                                  );
                                },
                              )}
                            </>
                          ) : (
                            <>
                              <tr>
                                <td colSpan={3}>
                                  <div className="w-100 fs-15 fw-bold d-flex justify-content-center">
                                    No Data found
                                  </div>
                                </td>
                              </tr>
                            </>
                          )}
                        </>
                      )}
                    </>
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>
          {!loading && totalRecords > 0 ? (
            <Pagination
              totalRecords={totalRecords}
              currentPage={page}
              handleCurrentPage={handleCurrentPage}
              handleNextPage={handleNextPage}
              handlePreviousPage={handlePreviousPage}
              handlePageLimit={handlePageLimit}
              pageLimit={pageLimit}
            />
          ) : (
            <></>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default AssignProductsToVendors;
